import type { BikeType } from './types'

// ── Bike type palette ────────────────────────────────────────────────────────
export const BIKE_COLORS: Record<BikeType, string> = {
  Sport: '#FF4438',
  ADV: '#34D26B',
  Sumo: '#FF9F1C',
  Cruiser: '#3B82F6',
}

// Unrated roads always render as translucent grey — bike type is irrelevant
// until the founder has rated the road.
export const UNRATED_COLOR = '#8B929E'

// Keyword matching (not exact-match): sheet labels like "ADV only",
// "Sport/ADV", "Dual sport" must still map to the right category.
function toBikeType(raw: string): BikeType | null {
  const t = raw.trim().toLowerCase()
  if (!t) return null
  if (t.includes('adv') || t.includes('adventure') || t.includes('dual')) return 'ADV'
  if (t.includes('sumo') || t.includes('supermoto')) return 'Sumo'
  if (t.includes('cruiser')) return 'Cruiser'
  if (t.includes('sport')) return 'Sport'
  return null
}

interface Hsl {
  h: number
  s: number
  l: number
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const n = parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return { h: h * 360, s, l }
}

function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
  const hh = ((h % 360) + 360) % 360 / 360
  if (s === 0) {
    const v = Math.round(l * 255)
    return [v, v, v]
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t: number): number => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  return [
    Math.round(channel(hh + 1 / 3) * 255),
    Math.round(channel(hh) * 255),
    Math.round(channel(hh - 1 / 3) * 255),
  ]
}

function hslToHex(hsl: Hsl): string {
  const [r, g, b] = hslToRgb(hsl)
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

// ── Weighted blend of bike colors (HSL space) ───────────────────────────────
export function blendBikeColors(types: string[]): string {
  const known: BikeType[] = []
  for (const raw of types ?? []) {
    if (typeof raw !== 'string') continue
    const t = toBikeType(raw)
    if (t && !known.includes(t)) known.push(t)
  }
  if (known.length === 0) return '#5A7A9A' // neutral blue-gray fallback
  if (known.length === 1) return BIKE_COLORS[known[0]]

  const weights: number[] =
    known.length === 2 ? [0.66, 0.34] : [0.5, 0.33, 0.17]

  let hX = 0, hY = 0 // circular hue average
  let s = 0, l = 0
  known.forEach((t, i) => {
    const rgb = hexToRgb(BIKE_COLORS[t])
    const c = rgbToHsl(...rgb)
    const w = weights[i] ?? 0
    const rad = (c.h * Math.PI) / 180
    hX += Math.cos(rad) * w
    hY += Math.sin(rad) * w
    s += c.s * w
    l += c.l * w
  })
  let h = (Math.atan2(hY, hX) * 180) / Math.PI
  if (h < 0) h += 360
  return hslToHex({ h, s, l })
}



// Opacity by overall rating: pure exponential anchored at 10/10 → 100% opacity
// and 5/10 → 10% opacity; ratings below 5 are pinned at the rating-5 value (10%).
export function ratingOpacity(overall: number | null): number {
  if (overall === null || Number.isNaN(overall)) return 0.45
  const x = Math.max(5, Math.min(10, overall)) / 10
  return 10 ** (2 * (x - 1))
}

// Basemap background per theme — used to bake translucency into an OPAQUE
// color so self-overlapping line segments can never brighten (stacking two
// 0.5-alpha segments renders ~0.75 alpha). Blending toward the background
// gives the identical visual result at opacity 1 with zero accumulation.
export const BASEMAP_BG: Record<'dark' | 'light', string> = {
  dark: '#0A0A0B',
  light: '#FAFAF8',
}

export function bakeOpacity(color: string, opacity: number, theme: 'dark' | 'light'): string {
  const bg = hexToRgb(BASEMAP_BG[theme])
  const fg = hexToRgb(color)
  const o = Math.max(0, Math.min(1, opacity))
  return (
    '#' +
    [0, 1, 2]
      .map((i) => Math.round(fg[i] * o + bg[i] * (1 - o)).toString(16).padStart(2, '0'))
      .join('')
  )
}

// Selected-road emphasis: push lightness + saturation up so the baked
// (background-blended) color pops off the map without changing its hue.
export function brightenColor(color: string): string {
  const [r, g, b] = hexToRgb(color)
  const hsl = rgbToHsl(r, g, b)
  hsl.s = Math.min(1, hsl.s * 1.25 + 0.05)
  hsl.l = Math.min(0.72, hsl.l + 0.18)
  return hslToHex(hsl)
}

// Rating does NOT affect hue or saturation — the color is purely the bike-type
// blend. Rating is expressed ONLY through opacity (ratingOpacity, baked in by
// bakeOpacity at the render layer).
export function ratingColor(types: string[], overall: number | null): string {
  // Unrated → flat grey, no bike-type color at all.
  if (overall === null || Number.isNaN(overall)) return UNRATED_COLOR
  return blendBikeColors(types)
}
